import api from "../api";
import { Permissao } from "../model/Usuario/Permissao";

export interface ContaServico {
    clientId: string;
    descricao: string;
    unidadeId: string;
    status: string;
    dataCriacao: string;
    dataAprovacao?: string;
    dataExpiracao?: string;
    dataAtualizacao?: string;
    acessoMultiUnidade: boolean;
    escopos: string[];
    responsavelId?: string;
    aprovadorId?: string;
    optOutEmailIntegracaoBancaAberta?: boolean;
}

export interface PerfilSimples {
    id: string;
    nome: string;
    descricao: string;
}

export interface PerfilContaServico {
    id?: string;
    nome: string;
    descricao: string;
    permissoes: Permissao[];
}

export interface SolicitacaoContaServico {
    id: string;
    descricao: string;
    unidadeId: string;
    acessoMultiUnidadeSolicitado: boolean;
    perfilSolicitado?: PerfilSimples;
    dataExpiracaoSolicitada?: string;
    status: string;
    dataCriacao: string;
    dataAtualizacao?: string;
    feedbackAprovador?: string;
    solicitanteId?: string;
}

export interface PaginatedResponse<T> {
    content: T[];
    pageable: {
        pageNumber: number;
        pageSize: number;
    };
    totalElements: number;
    totalPages: number;
    last: boolean;
}

export class ContaServicoViewModel {
    private static extractApiErrorMessage(error: unknown): string | null {
        const data = (error as any)?.response?.data;
        if (!data) return null;
        if (typeof data.message === 'string' && data.message.trim()) return data.message;
        if (Array.isArray(data) && data.length > 0) {
            const firstMessage = data[0]?.message;
            if (typeof firstMessage === 'string' && firstMessage.trim()) return firstMessage;
        }
        return null;
    }

    static async getSolicitacoesPendentes(page: number = 0, size: number = 10): Promise<PaginatedResponse<SolicitacaoContaServico>> {
        try {
            const response = await api.get('/contas-servico/solicitacoes/pendentes', {
                params: { page, size }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao buscar solicitações pendentes');
        }
    }

    static async aprovarSolicitacao(
        id: string,
        acessoMultiUnidade: boolean,
        dataExpiracao?: string
    ): Promise<ContaServico> {
        try {
            const response = await api.post(`/contas-servico/solicitacoes/${id}/aprovar`, {
                acessoMultiUnidade,
                dataExpiracao
            });
            return response.data;
        } catch (error: unknown) {
            throw error;
        }
    }

    static async rejeitarSolicitacao(id: string, motivo: string): Promise<void> {
        try {
            await api.post(`/contas-servico/solicitacoes/${id}/rejeitar`, null, {
                params: { motivo },
            });
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao rejeitar solicitação');
        }
    }

    static async getContasServico(page: number = 0, size: number = 10): Promise<PaginatedResponse<ContaServico>> {
        try {
            const response = await api.get('/contas-servico', {
                params: { page, size }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao listar contas de serviço');
        }
    }

    static async getContasAtivasEAprovadas(page: number = 0, size: number = 10): Promise<PaginatedResponse<ContaServico>> {
        try {
            const response = await api.get('/contas-servico/ativas-aprovadas', {
                params: { page, size }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao listar contas de serviço ativas e aprovadas');
        }
    }

    static async getMinhasContasServico(page: number = 0, size: number = 100): Promise<PaginatedResponse<ContaServico>> {
        try {
            const response = await api.get('/contas-servico/minhas', {
                params: { page, size }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao listar minhas contas de serviço');
        }
    }

    static async getAllContasServico(page: number = 0, size: number = 100): Promise<PaginatedResponse<any>> {
        try {
            const response = await api.get('/contas-servico', {
                params: { page, size }
            });
            
            return {
                content: response.data.content || [],
                pageable: {
                    pageNumber: response.data.pageable?.pageNumber || 0,
                    pageSize: response.data.pageable?.pageSize || size
                },
                totalElements: response.data.totalElements || 0,
                totalPages: response.data.totalPages || 0,
                last: response.data.last || false
            };
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao listar contas de serviço');
        }
    }

    private static handleApiError(error: unknown, defaultMessage: string): never {
        const apiMessage = ContaServicoViewModel.extractApiErrorMessage(error);
        throw new Error(apiMessage || defaultMessage);
    }

    static async solicitarContaServico(solicitacao: {
        descricao: string;
        unidadeId: string;
        acessoMultiUnidadeSolicitado: boolean;
        perfilSolicitadoId?: string;
        dataExpiracaoSolicitada?: string;
    }): Promise<SolicitacaoContaServico> {
        try {
            const response = await api.post('/contas-servico/solicitacoes', {
                descricao: solicitacao.descricao,
                unidadeId: solicitacao.unidadeId,
                acessoMultiUnidadeSolicitado: solicitacao.acessoMultiUnidadeSolicitado,
                perfilSolicitadoId: solicitacao.perfilSolicitadoId,
                dataExpiracao: solicitacao.dataExpiracaoSolicitada,
            });
            return response.data;
        } catch (error: unknown) {
            this.handleApiError(error, 'Erro ao enviar solicitação de conta de serviço');
        }
    }

    static async atualizarClientId(id: string, novoClientId: string): Promise<ContaServico> {
        try {
            const response = await api.patch(`/contas-servico/${id}/client-id`, { novoClientId });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao atualizar clientId');
        }
    }

    static async revogarConta(id: string): Promise<ContaServico> {
        try {
            const response = await api.patch(`/contas-servico/${id}/revogar`);
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao revogar conta de serviço');
        }
    }

    static async alterarResponsavel(contaId: string, novoResponsavelId: string | null): Promise<void> {
        try {
            const body = { novoResponsavelId };
            const response = await api.patch(`/contas-servico/${contaId}/responsavel`, body);
            return response.data;
        } catch (error) {
            const apiMessage = ContaServicoViewModel.extractApiErrorMessage(error);
            throw new Error(`Falha ao alterar responsável: ${apiMessage || 'Erro desconhecido'}`);
        }
    }

    static async atualizarOptOutEmail(contaId: string, optOutEmailIntegracaoBancaAberta: boolean): Promise<ContaServico> {
        try {
            const response = await api.patch(`/contas-servico/${contaId}/opt-out-email`, null, {
                params: { optOutEmailIntegracaoBancaAberta }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao atualizar preferências de email');
        }
    }

    static async cancelarExpiracao(contaId: string): Promise<ContaServico> {
        try {
            const response = await api.patch(`/contas-servico/${contaId}/cancelar-expiracao`);
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao cancelar expiração');
        }
    }

    static async getPermissoesCustomizadas(contaId: string): Promise<any> {
        try {
            const response = await api.get(`/contas-servico/${contaId}/permissoes`);
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao buscar permissões');
        }
    }

    static async adicionarPermissaoCustomizada(
        contaId: string,
        permissaoId: string,
        justificativa: string
    ): Promise<void> {
        try {
            await api.post(`/contas-servico/${contaId}/permissoes`, {
                permissaoId,
                justificativa
            });
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao adicionar permissão');
        }
    }

    static async removerPermissaoCustomizada(contaId: string, permissaoId: string): Promise<void> {
        try {
            await api.delete(`/contas-servico/${contaId}/permissoes/${permissaoId}`);
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao remover permissão');
        }
    }

    static async getPermissoesDisponiveis(): Promise<any[]> {
        try {
            const response = await api.get('/perfis/permissoes');
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao buscar permissões disponíveis');
        }
    }

    static async getLogsAcesso(
        contaId: string,
        page: number = 0,
        size: number = 20,
        dataInicio?: string,
        dataFim?: string
    ): Promise<PaginatedResponse<any>> {
        try {
            const response = await api.get(`/contas-servico/${contaId}/logs`, {
                params: { page, size, dataInicio, dataFim }
            });
            return response.data;
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao buscar logs de acesso');
        }
    }

    static async getPerfisContaServico(): Promise<PerfilContaServico[]> {
        try {
            const response = await api.get('/perfis-conta-servico');
            return response.data.map((perfil: any) => ({
                id: perfil.id,
                nome: perfil.nome,
                descricao: perfil.descricao,
                permissoes: (perfil.permissoes || []).map((permissao: any) => ({
                    id: permissao.id,
                    nome: permissao.nome,
                    descricao: permissao.descricao || ''
                }))
            }));
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao listar perfis de conta de serviço');
        }
    }

    static async createPerfilContaServico(perfil: PerfilContaServico): Promise<PerfilContaServico> {
        try {
            const payload = {
                nome: perfil.nome,
                descricao: perfil.descricao,
                permissoes: perfil.permissoes.map((p) => p.nome)
            };
            const response = await api.post('/perfis-conta-servico', payload);
            return {
                id: response.data.id,
                nome: response.data.nome,
                descricao: response.data.descricao,
                permissoes: (response.data.permissoes || []).map((permissao: any) => ({
                    id: permissao.id,
                    nome: permissao.nome,
                    descricao: permissao.descricao || ''
                }))
            };
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao criar perfil de conta de serviço');
        }
    }

    static async updatePerfilContaServico(id: string, perfil: PerfilContaServico): Promise<PerfilContaServico> {
        try {
            const payload = {
                nome: perfil.nome,
                descricao: perfil.descricao,
                permissoes: perfil.permissoes.map((p) => p.nome)
            };
            const response = await api.put(`/perfis-conta-servico/${id}`, payload);
            return {
                id: response.data.id,
                nome: response.data.nome,
                descricao: response.data.descricao,
                permissoes: (response.data.permissoes || []).map((permissao: any) => ({
                    id: permissao.id,
                    nome: permissao.nome,
                    descricao: permissao.descricao || ''
                }))
            };
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao atualizar perfil de conta de serviço');
        }
    }

    static async getPermissoesPerfisContaServico(): Promise<Permissao[]> {
        try {
            const response = await api.get('/perfis-conta-servico/permissoes');
            return (response.data || []).map((permissao: any) => ({
                id: permissao.id,
                nome: permissao.nome,
                descricao: permissao.descricao || ''
            }));
        } catch (error: unknown) {
            ContaServicoViewModel.handleApiError(error, 'Erro ao buscar permissões para perfis de conta de serviço');
        }
    }
}

export default ContaServicoViewModel;
